import React, { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";
import { debounce } from "lodash";
import { CgSpinner } from "react-icons/cg";
import { FaCircleCheck } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { HiOutlineArrowLongLeft, HiOutlineArrowLongRight } from "react-icons/hi2";
import { TbAlignCenter } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";

import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";

import api from "@/services/api";

import { Grid, GridColumn, GridItem } from "./plugin_grid";
import plugin_comment from "./plugin_comment";
import plugin_image from "./plugin_image";
import { FontSize } from "./plugin_fontSize";

import Menu from "./menu";

const Editor = ({ answer, setAnswer, readOnly = false }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [budgetCategory, setBudgetCategory] = useState("");
  const [title, setTitle] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [budgetDescription, setBudgetDescription] = useState("");
  const [epics, setEpics] = useState([]);

  const user = useSelector((state) => state.user);

  useEffect(() => {
    const fetchEpics = async () => {
      try {
        const { data } = await api.post("/mission_ao_epic/search", {
          mission_ao_answer_id: answer._id,
        });
        setEpics(data);
      } catch (error) {
        console.error("Failed to fetch epics:", error);
      }
    };
    fetchEpics();
  }, [answer._id]);

  const handleAddToBudget = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    if (!selectedText.trim()) {
      toast.error("Please select some text");
      return;
    }
    setBudgetDescription(selectedText);
    setIsBudgetModalOpen(true);
  };

  const handleDeleteEpic = async (epic) => {
    if (window.confirm("Are you sure you want to delete this budget item?")) {
      try {
        await api.remove(`/mission_ao_epic/${epic._id}`);
        setEpics(epics.filter((e) => e._id !== epic._id));
        toast.success("Budget item deleted successfully");
      } catch (error) {
        console.error("Failed to delete budget item:", error);
        toast.error("Failed to delete budget item");
      }
    }
  };

  const handleBudgetSubmit = async () => {
    const finalCategory = budgetCategory === "custom" ? customCategory : budgetCategory;
    if (!finalCategory) {
      toast.error("Please select or enter a category");
      return;
    }
    try {
      const payload = {
        category: finalCategory,
        title: title,
        description: budgetDescription,
        mission_ao_answer_id: answer._id,
        user_id: user?.id,
        user_name: user?.name,
        user_avatar: user?.avatar,
      };
      const { data } = await api.post(`/mission_ao_epic`, payload);
      setEpics([...epics, data]);

      // Highlight the selected text
      editor.chain().focus().setHighlight({ color: "#fef3c7" }).run();

      toast.success("Budget item added successfully");
    } catch (error) {
      console.error("Failed to add budget item:", error);
      toast.error("Failed to add budget item");
    }
    setIsBudgetModalOpen(false);
    setBudgetCategory("");
    setCustomCategory("");
    setBudgetDescription("");
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: true,
        image: false,
      }),
      Grid,
      GridColumn,
      GridItem,
      plugin_comment,
      plugin_image.configure({
        onImageClick: (node) => {
          setSelectedImage(node);
          setSelectedCommentId(null); // Masquer les commentaires
        },
      }),
      Underline,
      Image,
      // plugin_linter,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false }),
    ],
    content: answer.cctp_editor_content,
    onUpdate: async ({ editor }) => {
      const html = editor.getHTML();
      const updatedAnswer = { ...answer, cctp_editor_content: html };
      setIsSaving(true);
      try {
        const { data } = await api.put(`/mission_ao_answer/${answer._id}`, updatedAnswer);
        setAnswer(data);
        setLastSaveTime(new Date());
      } catch (error) {
        console.error("Failed to update answer:", error);
        toast.error("Failed to save changes");
      }
      setIsSaving(false);
    },
  });

  return (
    <div className="relative bg-white">
      {!readOnly && <Menu editor={editor} />}
      <div className="flex justify-end mt-2 mr-4 text-sm text-gray-500">
        {isSaving ? (
          <div className="flex items-center gap-2">
            <CgSpinner className="animate-spin" />
            Saving...
          </div>
        ) : lastSaveTime ? (
          <div>Last saved at {lastSaveTime.toLocaleTimeString()}</div>
        ) : null}
      </div>
      {editor && (
        <BubbleMenu
          shouldShow={({ editor }) => {
            return !isBudgetModalOpen && editor.isActive();
          }}
          className="flex overflow-hidden rounded border border-gray-200 bg-white shadow-lg"
          tippyOptions={{ duration: 100 }}
          editor={editor}>
          <button onClick={handleAddToBudget} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            Add a note/epic
          </button>
        </BubbleMenu>
      )}
      <div className="flex gap-4">
        <div className="prose max-w-none flex-1">
          <EditorContent editor={editor} />
        </div>
        <div className="w-64 border-l pl-4">
          <h3 className="font-semibold mb-2">Notes & Budget</h3>
          {epics.map((epic) => (
            <div key={epic._id} className="mb-2 p-2 bg-gray-50 rounded relative group">
              <button onClick={() => handleDeleteEpic(epic)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <IoMdClose className="text-gray-500 hover:text-red-500" />
              </button>
              <p className="text-sm pr-6">{epic.title}</p>
              <span className="text-xs text-gray-500 capitalize">{epic.category.replace(/_/g, " ")}</span>
              {/* {epic.description && <p className="text-xs text-gray-500 mt-1 italic">{epic.description}</p>} */}
            </div>
          ))}
        </div>
      </div>
      {isBudgetModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50"></div>
          <div className="bg-white p-6 rounded-lg shadow-xl w-96 relative z-50">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Add a note/budget</h2>
            <input
              type="text"
              value={budgetDescription}
              onChange={(e) => setBudgetDescription(e.target.value)}
              placeholder="Note"
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <select
              value={budgetCategory}
              onChange={(e) => setBudgetCategory(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4">
              <option value="">Select category</option>
              <option value="features">Features</option>
              <option value="integration">Integration</option>
              <option value="gestion_de_projet">Gestion de projet</option>
              <option value="guide">Guide</option>
              <option value="custom">Other</option>
            </select>
            {budgetCategory === "custom" && (
              <input
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Enter custom category"
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              />
            )}
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Advice/What do we do at Selego ?"
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded focus:ring-2 focus:ring-gray-400" onClick={() => setIsBudgetModalOpen(false)}>
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" onClick={handleBudgetSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
